'use client'

import { ChatHistoryItem, useUser, v1Client } from '@renderer/chat'
import { useAuth } from '@renderer/chat/providers'
import { AudioNote } from '@highlight/common'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer'
import { useAudioNotesStore } from '@renderer/stores/audio-notes'
import { useChatHistoryStore } from '@renderer/stores/chat-history'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { shareConversation } from '@renderer/utils/conversations/conversations'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getChatApiUrl } from '@shared/utils/ChatBackendUtils'

function useCopyChatShareLink() {
  return useMutation({
    mutationKey: ['copy-chat-share-link'],
    mutationFn: async (shareLink: string) => {
      const isStagingBackend = getChatApiUrl().includes('staging')
      const url = `https://highlightai.com/share/${shareLink}${isStagingBackend ? '?env=staging' : ''}`
      await navigator.clipboard.writeText(url)
    },
    onSuccess: (_, shareLink) => {
      AmplitudeRenderer.trackEvent('HL Chat Copy Link', {
        action: 'copy',
        share_link: `https://highlightai.com/share/${shareLink}`,
      })
    },
  })
}

function useGenerateChatShareLink() {
  const { addOrUpdateChat } = useChatHistoryStore()
  const { getAuthToken } = useAuth()

  return useMutation({
    mutationKey: ['generate-chat-share-link'],
    mutationFn: async (conversation: ChatHistoryItem) => {
      if (!conversation.id) return

      const formData = new FormData()
      formData.append('conversation_id', conversation.id)

      const response = await v1Client.POST('/conversations/{id}/shares', {
        params: {
          path: {
            id: conversation.id,
          },
        },
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (response.error) {
        throw new Error('Failed to generate share link')
      }

      const data = response.data.data
      const shareLink = data.sharedConversationId

      const isStagingBackend = getChatApiUrl().includes('staging')
      const url = `https://highlightai.com/share/${shareLink}${isStagingBackend ? '?env=staging' : ''}`
      await navigator.clipboard.writeText(url)

      return shareLink as string
    },
    onSuccess: (shareLink, chat) => {
      if (!shareLink || !chat.id) return

      addOrUpdateChat({
        id: chat.id,
        shares: [
          {
            createdAt: new Date().toISOString(),
            id: shareLink,
            sharedConversationId: shareLink,
            originalConversationId: chat.id,
          },
        ],
      })

      AmplitudeRenderer.trackEvent('HL Chat Copy Link', {
        action: 'create',
        conversation_id: chat.id,
        share_link: `https://highlightai.com/share/${shareLink}`,
      })
    },
    onError: (error, conversation) => {
      console.error('Failed to copy link:', error)

      AmplitudeRenderer.trackEvent('HL Chat Copy Link Error', {
        conversation_id: conversation.id,
        error: error.message,
      })
    },
  })
}

function useCopyAudioShareLink() {
  return useMutation({
    mutationKey: ['copy-audio-share-link'],
    mutationFn: async (shareLink: string) => {
      await navigator.clipboard.writeText(shareLink)
    },
    onSuccess: (_, shareLink) => {
      AmplitudeRenderer.trackEvent('HL Chat Audio Note Copy Link', {
        action: 'copy',
        share_link: shareLink,
      })
    },
  })
}

interface GenerateAudioShareLinkInput {
  audioNote: AudioNote
  /** Off when the link is made public for another destination (e.g. Slack) rather than to paste. */
  copyToClipboard?: boolean
}

function useGenerateAudioShareLink() {
  const user = useUser()
  const { updateAudioNote } = useAudioNotesStore()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['generate-chat-share-link'],
    mutationFn: async ({ audioNote, copyToClipboard = true }: GenerateAudioShareLinkInput) => {
      if (!audioNote || !user?.id) return

      const shareLink = await shareConversation(audioNote)

      if (shareLink) {
        AudioNoteIpcRenderer.updateAudioNote({
          id: audioNote.id,
          updates: {
            shareLink,
          },
        })

        if (copyToClipboard) {
          await OverlayIpcRenderer.writeTextToClipboard(shareLink)
        }
      }

      return shareLink as string
    },
    onSuccess: (shareLink, { audioNote }) => {
      if (!shareLink || !audioNote.id) return

      queryClient.invalidateQueries({ queryKey: ['audio-notes'] })
      updateAudioNote({ id: audioNote.id, shareLink })

      AmplitudeRenderer.trackEvent('HL Chat Audio Note Copy Link', {
        action: 'create',
        conversation_id: audioNote.id,
        share_link: shareLink,
      })
    },
    onError: (error, { audioNote }) => {
      console.error('Failed to copy link:', error)

      AmplitudeRenderer.trackEvent('HL Chat Audio Note Copy Link Error', { conversation_id: audioNote.id, error })
    },
  })
}

export { useCopyChatShareLink, useGenerateChatShareLink, useCopyAudioShareLink, useGenerateAudioShareLink }
